import PropTypes from 'prop-types';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import cx from 'classnames';
import debounce from 'lodash.debounce';

import { withRouter } from 'core/libs/router';

import { resolveScopedStyles } from 'core/utils/styled-jsx';
import { getCurrentMenuMatches } from 'core/utils/url-helper';
import themePropTypes from 'core/utils/prop-types/theme';

import withTheme from 'core/hocs/withTheme';
import withBreakpoint from 'core/hocs/withBreakpoint';
import { Mobile } from 'core/components/breakpoint';
import Bouesque from 'core/components/Bouesque';
import ShapkaMenuItems from 'core/components/ShapkaMenuItems';
import Dropdown from './Dropdown';
import Auth from 'core/components/Auth';

import calcStartHideIndex from './utils';

import styles from './index.styl';

const bodyOverflowScope = resolveScopedStyles(
  <scope>
    <style jsx>{`
      :global(body)
        overflow hidden
    `}</style>
  </scope>
);


function ShapkaMenu(props, context) {
  const {
    items,
    theme,
    isDesktop,
    isMobile,
    additional,
    setIsOpen,
    isOpen,
    menuInjection,
    isAuthorized,
    renderers,
    isFirstShapkaForPremium,
    location,
  } = props;

  const {
    ramblerId: {
      useAuthRedirect,
    },
  } = context;

  const navRef = useRef(null);
  const [moreMenu, setMoreMenu] = useState(items.filter(item => !item.show));
  const hideIndex = items.findIndex(item => !item.show);
  const showItems = (isMobile || isFirstShapkaForPremium) ? items : items.filter(item => item.show);
  const [startHideIndex, setStartHideIndex] = useState(hideIndex === -1 ? items.length : hideIndex);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resizeMenu = useCallback(debounce(() => {
    const navWidth = navRef.current.offsetWidth;
    const itemsWidths = [...navRef.current.childNodes].map(item => item.offsetWidth);
    const index = calcStartHideIndex(navWidth, itemsWidths, startHideIndex);
    if (showItems.length >= index) {
      setStartHideIndex(index);
      setMoreMenu(items.slice(index));
    }
  }, 100), []);

  useEffect(() => {
    if (process.env.BROWSER_RUNTIME && isDesktop && !isFirstShapkaForPremium) {
      resizeMenu();
    }
  }, [resizeMenu, isDesktop, isAuthorized, isFirstShapkaForPremium]);

  useEffect(() => {
    let itemObserver;
    if (process.env.BROWSER_RUNTIME && isDesktop && !isFirstShapkaForPremium) {
      window.addEventListener('resize', resizeMenu);

      itemObserver = new IntersectionObserver(entries => {
        entries.forEach(({ intersectionRatio }) => {
          if (intersectionRatio <= 0.5) {
            resizeMenu();
          }
        });
      }, {
        root: navRef.current,
        threshold: [0.5],
      });

      navRef.current.childNodes.forEach(item => {
        itemObserver.observe(item);
      });
    }

    return () => {
      if (process.env.BROWSER_RUNTIME && isDesktop && !isFirstShapkaForPremium) {
        window.removeEventListener('resize', resizeMenu);
        itemObserver.disconnect();
      }
    };
  }, [resizeMenu, isDesktop, isFirstShapkaForPremium]);

  const currentMatches = useMemo(
    () => getCurrentMenuMatches(items, location),
    [location, items]
  );

  if (!items.length) {
    return null;
  }

  const {
    dropdown: dropdownAtoms,
    dropdown: {
      link: linkAtoms,
    },
    burger: {
      Icon: Burger,
      width: burgerWidth,
      height: burgerHeight,
    },
    close: {
      Icon: Close,
      width: closeWidth,
      height: closeHeight,
    },
    padding,
    height,
    menu,
  } = theme.controls.shapka;

  const dropdown = isFirstShapkaForPremium ? dropdownAtoms.premiumTopic : dropdownAtoms;

  const menuIcon = (isOpen && dropdown.trigger === 'click')
    ? (
      <Close
        width={closeWidth}
        height={closeHeight}
      />
    ) : <Burger width={burgerWidth} height={burgerHeight} />;

  const onOpen = () => {
    setIsOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
  };

  return (
    <div
      className={cx(
        styles.menu,
        menu.centered && isDesktop && styles._menuCentered,
        menu.right && isDesktop && styles._menuRight,
        isFirstShapkaForPremium && styles._premium,
      )}
    >
      <style jsx>{`
        .${styles.nav}
          margin ${menu.navMargin}
          .${styles._premium} &
            margin 0

        .${styles.menuContent}
          :global(.mobile) &
            padding ${padding}
            background ${dropdown.background}
            height calc(100vh - ${height}px)

        .${styles.burger}
          color ${linkAtoms.idle.color}
        `}</style>
      <Mobile>
        <div
          className={styles.burger}
          onClick={() => setIsOpen(!isOpen)}
        >
          {menuIcon}
        </div>
      </Mobile>
      {(isDesktop || (isMobile && isOpen)) && (
        <div className={styles.menuContent}>
          {additional && <Mobile><div className={styles.additional}>{additional}</div></Mobile>}
          {!isAuthorized && !useAuthRedirect && <Mobile><Auth showPopUp /></Mobile>}
          {!menu.hideBouesque && (
            <Mobile>
              <div className={styles.search}>
                <Bouesque />
              </div>
            </Mobile>
          )}
          <nav
            ref={navRef}
            className={cx(
              styles.nav,
              !dropdown.fullwidth && isOpen && styles._overflowVisible
            )}
          >
            {(isDesktop && (startHideIndex < items.length) || (isDesktop && isFirstShapkaForPremium)) && (
              <Dropdown
                isFirstShapkaForPremium={isFirstShapkaForPremium}
                onClose={onClose}
                onOpen={onOpen}
                navRef={navRef}
                moreMenu={isFirstShapkaForPremium ? items : moreMenu}
                currentItem={currentMatches[0]}
                menuIcon={menuIcon}
              />
            )}
            {(!isFirstShapkaForPremium || isMobile) && (
              <ShapkaMenuItems
                items={showItems}
                currentItem={currentMatches[0]}
                startHideIndex={startHideIndex}
                {...!isMobile && { isNavItem: true }}
                menuInjection={menuInjection}
                renderers={renderers}
              />
            )}
          </nav>
        </div>
      )}
      {isOpen && isMobile && <bodyOverflowScope.styles />}
    </div>
  );
}

ShapkaMenu.contextTypes = {
  ramblerId: PropTypes.object,
};

ShapkaMenu.propTypes = {
  isFirstShapkaForPremium: PropTypes.bool,
  /** @ignore */
  isDesktop: PropTypes.bool,
  /** @ignore */
  isMobile: PropTypes.bool,
  /**
   * @ignore
   */
  theme: themePropTypes(`
    {
      colors: {
        content
      },
      controls: {
        shapka: {
          subnav,
          padding,
          height,
          burger: {
            Icon,
            width,
            height,
          },
          close: {
            Icon,
            width,
            height,
          },
          link: {
            font,
            textTransform,
            idle: {
              color,
            },
          },
          dropdown: {
            background,
            height,
            boxShadow,
            trigger,
            fullwidth,
            padding,
            position,
          },
          menu: {
            centered,
            right,
            hideBouesque,
          },
        }
      }
    }
  `),

  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,

  additional: PropTypes.node,
  setIsOpen: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  items: PropTypes.array,
  menuInjection: PropTypes.objectOf(PropTypes.func.isRequired),
  isAuthorized: PropTypes.bool,
  renderers: PropTypes.objectOf(PropTypes.func),
};

export default withBreakpoint(withTheme(withRouter(ShapkaMenu)));
